import React, { useEffect, useState } from 'react';
import { StatusFormSetting } from '../../../AssetDetails/AssetTabs';
import '../../../../Forms.scss'
import { closeTriggerKeyValuePair, openTriggerKeyValuePair } from '../../../SiteSummary/constants';

interface FieldState {
    [key: string]: string;
}

type SetPageFunction = React.Dispatch<React.SetStateAction<{ [key: string]: FieldState; }>>;
type SetTabPageFunction = React.Dispatch<React.SetStateAction<string>>
type SetChangeFieldFunction = React.Dispatch<React.SetStateAction<{
    [formName: string]: Set<string>;
}>>

type DropdownOption = {
    text: string;
    value: number;
    status?: any;
};

interface PFCFormProps {
    formState: { [key: string]: FieldState };
    setFormState: SetPageFunction;
    setTabDivHeight: SetTabPageFunction;
    PFCFormData: StatusFormSetting[] | undefined;
    changedFields: { [formName: string]: Set<string> };
    setChangedFields: SetChangeFieldFunction;
    formNames: string;
    isToggle: boolean;
}
interface InitialValues {
    [formName: string]: {
        [fieldName: string]: string;
    };
}


const PfcFormTablet: React.FC<PFCFormProps> = ({ formNames, formState, setFormState, setTabDivHeight, PFCFormData, changedFields, setChangedFields, isToggle }) => {
    const [initialValues, setInitialValues] = useState<{ [formName: string]: { [fieldName: string]: string } }>({});
    const FormName = formNames

    useEffect(() => {
        setTabDivHeight("700px");
    }, []);

    useEffect(() => {
        // Initialize initial values when the component mounts or when form data is initialized
        const initialValues: InitialValues = {};
        // Initialize initial values for each form and field
        // Assuming formState has been initialized with initial form data
        Object.keys(formState).forEach(formName => {
            initialValues[formName] = {};
            Object.keys(formState[formName]).forEach(fieldName => {
                initialValues[formName][fieldName] = formState[formName][fieldName];
            });
        });
        setInitialValues(initialValues);
        setChangedFields({})
    }, [formNames, isToggle]);

    // Handle input change for a specific field in a specific form
    const handleInputChange = (formName: string, fieldName: string, value: string) => {

        setFormState((prevFormState) => ({
            ...prevFormState,
            [formName]: {
                ...prevFormState[formName],
                [fieldName]: value,
            },
        }));
    };


    // Function to identify change in field value
    const handleFieldChange = (formName: string, fieldName: string, newValue: string) => {
        const initialValue = initialValues[formName]?.[fieldName];
        if (initialValue !== newValue) {
            setChangedFields((prevChangedFields) => ({
                ...prevChangedFields,
                [formName]: new Set<string>([...(prevChangedFields[formName] || []), fieldName]),
            }));
        } else if (initialValue === newValue) {
            setChangedFields((prevChangedFields) => {
                const updatedFields = new Set<string>(prevChangedFields[formName] || []);
                updatedFields.delete(fieldName); // Remove the field name if the value reverts back to the initial value
                return {
                    ...prevChangedFields,
                    [formName]: updatedFields,
                };
            });
        }
    };

    const isFieldGrayedOut = (fieldName: string): boolean => {
        const openTrigger = formState[FormName]?.openTrigger;
        const closeTrigger = formState[FormName]?.closeTrigger;

        const getOpenTriggerValues = (): number[] => {
            return openTriggerKeyValuePair.map(item => item.value);
        };

        const getCloseTriggerValues = (): number[] => {
            return closeTriggerKeyValuePair.map(item => item.value);
        };

        const normalize = (val: string) =>
            val?.toLowerCase().replace(/[:]/g, "").replace(/\s+/g, "").trim();

        const currentField = PFCFormData?.find(field => field.name === fieldName);
        const currentLabelId = currentField?.id ?? 0;

        // Get Open Trigger Label
        const openTriggerField = PFCFormData?.find(field => field.name === "openTrigger");
        const openTriggerOptions = openTriggerField?.options as DropdownOption[] | undefined;

        const normalizedOpenTriggerLabel =
            openTriggerOptions?.find(opt => opt.value.toString() === openTrigger)?.value.toString() ?? ""

        const openTriggerId = openTriggerKeyValuePair.find(
            item => normalize(item.key) === normalizedOpenTriggerLabel
        )?.value;

        // Get Close Trigger Label
        const closeTriggerField = PFCFormData?.find(field => field.name === "closeTrigger");
        const closeTriggerOptions = closeTriggerField?.options as DropdownOption[] | undefined;

        const normalizedCloseTriggerLabel =
            closeTriggerOptions?.find(opt => opt.value.toString() === closeTrigger)?.value.toString() ?? ""

        const closeTriggerIds = closeTriggerKeyValuePair
            .filter(item => normalize(item.key) === normalizedCloseTriggerLabel)
            .map(item => item.value);

        const openTriggerRelatedLabels = getOpenTriggerValues();
        const closeTriggerRelatedLabels = getCloseTriggerValues();

        if (openTriggerRelatedLabels.includes(currentLabelId)) {
            return currentLabelId !== openTriggerId;
        }

        if (closeTriggerRelatedLabels.includes(currentLabelId)) {
            return !closeTriggerIds.includes(currentLabelId); // ← allow multiple "time" values
        }

        return false;
    };

    return (
        <div>
            {/* Plunger Form */}
            <div style={{ display: 'flex', flexWrap: 'wrap', columnGap: '10px', rowGap: '6px', justifyContent: 'flex-start' }}>
                {PFCFormData?.map(({ id, value, name, label, type, options, enable, description, isGrayedOut, unit }) => (
                    enable ?
                        <div key={id} className='io-container' style={{ flex: '1 1 20%', minWidth: '150px' }}>
                            {enable && (
                                <div className="tooltip-container">
                                    <label
                                        style={{ display: 'block', marginBottom: '2px', marginLeft: "3px" }}
                                    >
                                        {label}
                                    </label>
                                    {description && <span className="tooltip-text">{description}</span>}
                                </div>
                            )}
                            {type !== "dropdown" ?

                                <div className="relative w-full">
                                    {/* Unit Text - Positioned inside the input */}
                                    {unit && (
                                        <div
                                            className="absolute inset-y-0 right-2 flex items-center pointer-events-none input-with-unit"
                                            style={{
                                                color: isGrayedOut
                                                    ? "#4A5463"
                                                    : "var(--Primary-Indigo-500, #0094BD)",
                                                fontSize: "16px",
                                            }}
                                        >
                                            | {unit}
                                        </div>
                                    )}

                                    <input
                                        type={name.toLocaleLowerCase() === "iccid" ? "string" : "number"}
                                        value={formState[FormName] ? formState[FormName][name] : value}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            display: enable ? "" : "none", padding: "6px", color: 'white', borderRadius: '8px', textAlign: 'right', backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : (isGrayedOut)
                                                    ? "#131F2E"
                                                    : "rgb(5 17 35)",
                                            width: "100%",
                                            outline: isGrayedOut ? "1px solid #4A5463" : "1px solid var(--Primary-Indigo-500, #0094BD)",
                                            paddingRight: unit ? "5rem" : "6px"
                                        }}
                                        placeholder={label}
                                        disabled={isGrayedOut}
                                    /></div> : <select
                                        disabled={isGrayedOut}
                                        value={formState[FormName] ? formState[FormName][name] : value}
                                        onChange={(e) => {
                                            const newValue = e.target.value;
                                            handleInputChange(FormName, name, newValue);
                                            handleFieldChange(FormName, name, newValue);
                                        }}
                                        style={{
                                            display: enable ? "" : "none", padding: "6px", color: 'white', borderRadius: '8px', textAlign: 'left', backgroundColor: changedFields[FormName]?.has(name)
                                                ? "rgb(208 196 64)"
                                                : (isGrayedOut)
                                                    ? "#131F2E"
                                                    : "rgb(5 17 35)",
                                            width: "100%",
                                            outline: isGrayedOut ? "1px solid #4A5463" : "1px solid var(--Primary-Indigo-500, #0094BD)"
                                        }}
                                    >
                                    {options?.map((option: any) => (
                                        <option key={option.value} value={option.value}>
                                            {option.text}
                                        </option>
                                    ))}
                                </select>}
                        </div> : <></>
                ))}
            </div>
        </div>

    );
};

export default PfcFormTablet;
